import type { ProductConfigSchema } from './useProductConfigSchemaCoCo.js';
import type {
  AvailableInstallationDatesResponseItem,
  InstallationAvailabilityState,
} from '../../../common/types/installationDatesCoCo.js';
import type { ProductOfferingCartItems } from '../../../common/types/shoppingBasketCoCo.js';
import type { ProductOfferingsResponse } from '../../../generated/api/productOfferingsResponse.js';
import type { ContactsUpdateMethods } from '../placeOrderTypes.js';
import type { PlaceOrderItemUnitCoCo } from '../placeOrderTypesCoCo.js';
import type { ContactOrPurposeHandle } from '../steps/products/PlaceOrderContactOrPurpose/PlaceOrderContactOrPurpose.js';
import type React from 'react';
import type { ForwardRefExoticComponent, RefAttributes, RefObject } from 'react';

/**
 * Imperative handle exposed by every product config component (forwardRef).
 * Used by ProductsStepCoCo to validate before advancing.
 */
export interface ConfigHandle {
  validate: () => Promise<boolean>;
  scrollToFirstError: () => void;
  getErrorCount: () => number;
}

export interface InstallationContactHandle {
  validate: () => Promise<boolean>;
  scrollToFirstError: () => void;
  getErrorCount: () => number;
}

export interface InstallationDateSelectionProps {
  availableInstallationDates: AvailableInstallationDatesResponseItem[];
  installationAvailability?: InstallationAvailabilityState;
  availabilityCheckedDate?: string;
  validationMessage?: string;
  /** Show the generic COCO 3217 guidance for fibre/Kuitu orders. */
  showExistingSubscriptionTerminationNotice?: boolean;
  onCheckAvailability?: () => boolean | Promise<boolean>;
  onConfirmInstallationTimeSlots?: (timeSlotIds: Record<string, string>) => boolean | Promise<boolean>;
}

export interface ProductConfigProps {
  basketItem: ProductOfferingCartItems;
  orderItem: PlaceOrderItemUnitCoCo;
  schema: ProductConfigSchema;
  /** Available numbers shown in the phone number picker */
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  onLoadMoreNumbers: () => void;
  /** Numbers already taken/reserved; shown as disabled in the picker */
  disabledPhoneNumbers: string[];
  initialPhoneNumber?: string;
  /** Refs to contact forms — keyed by `${basketItemId}-${basketItemIndex}` */
  contactFormRefs: RefObject<Record<string, ContactOrPurposeHandle | null>>;
  /** Refs to installation contact forms — keyed by `${basketItemId}-${basketItemIndex}` */
  installationContactRefs?: RefObject<Record<string, InstallationContactHandle | null>>;
  contactsUpdateMethods: ContactsUpdateMethods;
  /** Cart item IDs in the product group, used by product-level selectors. */
  productGroupCartItemIds?: string[];
  /** Product-level selectors are rendered once, by the first unit in the group. */
  isProductGroupFirstUnit?: boolean;
  onProductOfferingRuntimeCharacteristicChange?: (groupCartItemIds: string[], selectedValue: string) => void;
  onPlanProductOfferingSpecRuntimeCharacteristicChange?: (cartItemId: string, selectedValue: string) => void;
  /**
   * Keys (`${basketItemId}-${basketItemIndex}`) of the other order item units that are configured
   * by this same section, e.g. the remaining units of a quantity based accessory.
   */
  accessorySharedUnitKeys?: string[];
}

/** React.forwardRef component that accepts ProductConfigProps and exposes ConfigHandle */
export type ForwardRefConfigComponent = ForwardRefExoticComponent<ProductConfigProps & RefAttributes<ConfigHandle>>;

export enum ProductConfigScope {
  UNIT = 'unit',
  PRODUCT = 'product',
}

export interface AddonContentSelection {
  selectedCharacteristics: Record<string, string>;
  onCharacteristicChange: (characteristicName: string, value: string) => void;
}

export type RenderAddonContent = (
  offer: ProductOfferingsResponse,
  addonSubcategory: string,
  selection: AddonContentSelection
) => React.ReactNode;

export interface ProductConfigDescriptor {
  component: ForwardRefConfigComponent;
  /** Whether the product supports DAC installation availability and time-slot selection. */
  supportsInstallationAvailability?: boolean;
  renderAddonContent?: RenderAddonContent;
  /**
   * When true, the purpose of use belongs to this product regardless of what the catalogue offering
   * declares. The field is always offered in the configuration section, and the order always carries
   * a purpose of use, defaulting to "user unknown" when the customer assigned neither a user nor a
   * purpose of use.
   */
  alwaysCollectPurposeOfUse?: boolean;
  /**
   * How many configuration sections are rendered for an ordered quantity:
   *  - `unit` (default): one section per ordered unit
   *  - `product`: a single section for the whole quantity; its values are applied to every unit
   */
  configScope?: ProductConfigScope;
  /** Heading of a configuration section. Defaults to the subscription heading of the unit. */
  getSectionHeading?: (unitNumber: number) => string;
}
